import React from 'react';
import { motion } from 'framer-motion';
import { MagicCard } from '@/components/lib/magicui/MagicCard';
import { useReducedMotion } from '@/hooks/useReducedMotion';

export default function AboutAntiPersona() {
  const prefersReducedMotion = useReducedMotion();

  // "Not For" items
  const notForItems = [
    'You just want the cheapest possible logo and website so you can "have something up."',
    "You're hoping design alone will fix an offer that hasn't sold yet or has no real demand.",
    "You're not willing to make decisions about your positioning, pricing, or who you actually want to work with.",
    'You want to keep full control of every pixel instead of trusting an operator to architect the experience.',
    "You're looking for a big agency team and layers of account management instead of direct access to the person doing the work.",
    'You want a quick cosmetic refresh, not a serious conversation about how your domain can become a real asset.',
  ];

  // "For" items
  const forItems = [
    'You already sell a service, coaching or consulting offer, and you want the business behind it to stop eating your week.',
    'You value strategy as much as design, and you\'re curious what AI can actually do for a business your size.',
    'You want a partner who has actually started, run, and sold businesses - not just designed sites for other people.',
    "You're ready to invest in a premium site and the system that runs behind it.",
    'You prefer working directly with the person making decisions, not through multiple layers of an agency.',
    'You want your premium domain to function like an asset that brings clients in, not a digital business card that sits there.',
  ];

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headingItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const leftPanelVariants = {
    hidden: { opacity: 0, x: prefersReducedMotion ? 0 : -24 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const rightPanelVariants = {
    hidden: { opacity: 0, x: prefersReducedMotion ? 0 : 24 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <motion.section
      id="fit-check"
      aria-label="Who Attain Domains is for and not for"
      variants={sectionVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className="relative w-full py-20 md:py-24 bg-slate-950 text-slate-50 overflow-hidden"
    >
      {/* Decorative spotlight elements */}
      <div className="pointer-events-none absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />
      
      {/* Top vignette */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/20 to-transparent" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Heading block */}
          <motion.div
            variants={headingVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center mb-16 md:mb-20"
          >
            <motion.div variants={headingItemVariants}>
              <p className="text-xs md:text-sm font-semibold tracking-wide text-slate-400 uppercase mb-4">
                FIT CHECK
              </p>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50 mb-6">
                This is not for everyone. And that's on purpose.
              </h2>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
                Great domains deserve serious launches. This section exists to help you decide if
                Attain Domains is the right partner for where you are in your business - and where
                you want it to go.
              </p>
            </motion.div>
          </motion.div>

          {/* Two-panel grid */}
          <motion.div
            variants={gridVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="grid gap-8 lg:grid-cols-2 items-stretch"
          >
            {/* Left Panel: NOT FOR */}
            <motion.div variants={leftPanelVariants}>
              <MagicCard className="h-full">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/70 backdrop-blur-sm p-8 h-full flex flex-col">
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-200 mb-6">
                    This is NOT for you if…
                  </h3>
                  <ul className="space-y-4 text-sm md:text-base text-slate-300 leading-relaxed">
                    {notForItems.map((item, index) => (
                      <li key={index} className="flex gap-3 items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </MagicCard>
            </motion.div>

            {/* Right Panel: FOR (emphasized) */}
            <motion.div variants={rightPanelVariants} className="lg:scale-[1.02]">
              <MagicCard className="h-full">
                <div className="rounded-2xl border border-slate-700 bg-gradient-to-br from-[#0000FF]/15 via-slate-900/80 to-slate-950 backdrop-blur-sm p-8 h-full flex flex-col">
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-50 mb-6">
                    This IS for you if…
                  </h3>
                  <ul className="space-y-4 text-sm md:text-base text-slate-100 leading-relaxed">
                    {forItems.map((item, index) => (
                      <li key={index} className="flex gap-3 items-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0000FF] mt-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </MagicCard>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

